import React, { useState } from 'react';
import { GlowingEffect } from './GlowingEffect';

export interface DockItem {
  id: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

interface DockItemProps {
  item: DockItem;
  isHovered: boolean;
  isActive?: boolean;
  onHover: (id: string | null) => void;
}

const DockItemComponent: React.FC<DockItemProps> = ({ item, isHovered, isActive, onHover }) => {
  return (
    <div
      className="relative group"
      onMouseEnter={() => onHover(item.id)}
      onMouseLeave={() => onHover(null)}
    >
      <div
        className={`
          relative flex items-center justify-center
          w-12 h-12 rounded-full
          backdrop-blur-[12px]
          border transition-all duration-300 ease-out
          cursor-pointer overflow-hidden
          ${isActive 
            ? 'bg-foreground text-background border-transparent' 
            : 'bg-foreground/5 text-muted-foreground border-foreground/10 hover:bg-foreground/10 hover:border-foreground/20 hover:text-foreground'
          }
          ${isHovered && !isActive ? '-translate-y-2 scale-110' : ''}
        `}
        onClick={item.onClick}
      >
        <GlowingEffect
          spread={30}
          glow={isActive || isHovered}
          disabled={false}
          proximity={60}
          inactiveZone={0.01}
          borderWidth={1.5}
          variant={isActive ? "white" : "default"}
        />
        <div className={`
          relative z-10 transition-all duration-300
          ${isHovered ? 'scale-105' : ''}
        `}>
          {item.icon}
        </div>
      </div>
      
      {/* Tooltip */}
      <div className={`
        absolute -top-12 left-1/2 transform -translate-x-1/2
        px-3 py-1.5 rounded-lg
        bg-foreground backdrop-blur-md
        text-background text-[10px] font-bold tracking-[0.2em] uppercase
        border border-foreground/10
        transition-all duration-200
        pointer-events-none
        whitespace-nowrap
        z-50
        ${isHovered 
          ? 'opacity-100 translate-y-0' 
          : 'opacity-0 translate-y-2'
        }
        shadow-xl
      `}>
        {item.label}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2">
          <div className="w-2 h-2 bg-foreground rotate-45 border-r border-b border-foreground/10"></div>
        </div>
      </div>
    </div>
  );
};

interface MinimalistDockProps {
  items: DockItem[];
  activeId?: string;
  className?: string;
}

export const MinimalistDock: React.FC<MinimalistDockProps> = ({ items, activeId, className = "" }) => {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  return (
    <div className={`fixed bottom-10 left-1/2 -translate-x-1/2 z-[100] ${className}`}>
      <div className="relative rounded-full">
        <GlowingEffect
          spread={100}
          glow={true}
          disabled={false}
          proximity={150}
          inactiveZone={0.01}
          borderWidth={2}
          className="rounded-full"
        />
        <div className={`
          flex items-end gap-3 px-6 py-4
          rounded-full
          bg-background/80 backdrop-blur-2xl
          border border-border/50
          shadow-[0_25px_50px_-12px_rgba(0,0,0,0.1)]
          transition-all duration-500 ease-out
          relative overflow-hidden
          ${hoveredItem ? 'scale-[1.02]' : ''}
        `}>
          {items.map((item) => (
            <DockItemComponent
              key={item.id}
              item={item}
              isActive={activeId === item.id}
              isHovered={hoveredItem === item.id}
              onHover={setHoveredItem}
            />
          ))}
        </div>
        
        <div className="absolute top-full left-0 right-0 h-12 overflow-hidden pointer-events-none">
          <div className={`
            flex items-start gap-3 px-6 py-4
            rounded-full
            bg-foreground/5 backdrop-blur-xl
            border border-foreground/5
            opacity-5
            transform scale-y-[-1]
            transition-all duration-500 ease-out
            ${hoveredItem ? 'scale-[1.02] scale-y-[-1.02]' : ''}
          `}>
            {items.map((item) => (
              <div
                key={`reflection-${item.id}`}
                className={`
                  flex items-center justify-center
                  w-12 h-12 rounded-full
                  transition-all duration-300 ease-out
                  ${hoveredItem === item.id ? 'scale-110 -translate-y-1' : ''}
                `}
              >
                <div className="text-foreground">
                  {item.icon}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};